import {
  BusinessFormLabelModifier,
  CandidateReasonOfInspectionByExternalExpertModifier,
  OperationFormLabelModifier,
} from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import {
  createCsv,
  formatNumber,
  handleZeroValueWithNegativeCount,
} from '@/utils';
import { NEST_ITEM_CONNECTOR } from '@/utils/constants';
import {
  FundSheetTypeLabelModifier,
  ReviewTypeLabelModifier,
  SheetTypeLabelModifier,
} from '@/utils/enum';
import type { z } from 'zod';
import type {
  ReportFundSheetListModel,
  ReportReviewSheetListModel,
  ReportSegmentSheetListModel,
} from '../models/project';
import { formatOwnershipRatioPercentage } from './formatter/rs-ledger';

// 各sheetTypeの返り値型を明確に定義
type BaseCSVRow = {
  sheetType: string;
  projectNumber: number | null;
  fiscalYear: number;
  name: string;
  overview: string;
  isLocked: string;
  isPublished: string;
  ministryName: string | null;
  startYear: number | null;
  endYear: number | null;
  organizationLabel: string;
  purpose: string;
  task: string;
  finalizedAmountForInitialBudget: string;
  previousYearExecutedTotalAmount: string;
  previousYearExecutionRate: string;
  finalizedAmountForSupplementaryBudget: string;
  finalizedAmountForCarriedForward: string;
  finalizedAmountForReserve: string;
  finalizedAmount: string;
  requestedAmount: string;
  mainPayment: string;
  paymentAmount: string;
  id: string;
};

type RSCSVRow = BaseCSVRow & {
  skipAdministrativeReformTeamEvaluation: string;
  inspectionByExternalExpertLastImplementedFiscalYear:
    | number
    | null
    | undefined;
  inspectionByExternalExpertReviewType: string;
  candidateReasonsOfInspectionByExternalExpert: string;
};

type SSCSVRow = Omit<
  BaseCSVRow,
  | 'finalizedAmountForCarriedForward'
  | 'finalizedAmountForReserve'
  | 'finalizedAmount'
> & {
  skipAdministrativeReformTeamEvaluation: string;
};

type KSCSVRow = {
  organizationLabel: string;
  sheetType: string;
  fundSheetType: string;
  projectNumber: string;
  fiscalYear: number;
  fundSheetNumber: string;
  fundSheetBranchNumber: string;
  name: string;
  overview: string;
  isLocked: string;
  isPublished: string;
  ministryName: string;
  startYear: number | null;
  endYear: number | null;
  purpose: string;
  task: string;
  fundOwner: string;
  fiscalYearOfCreation: string;
  endDateOfNewApplicationReceiving: string;
  endDate: string;
  operationForms: string;
  businessForms: string;
  beginningOfTheFundBalance: string;
  ownershipRatio: string;
  previousYearOfAmountReturnedToTheNationalTreasuryReturn: string;
  previousYearOfTotalExpense: string;
  previousYearOfAdministrativeExpenses: string;
  id: string;
  mainPayment: string;
  previousYearAdministrativeExpenseRate: string;
  previousYearDivergenceRate: string;
  paymentAmount: string;
  previousYearOfTotalIncome: string;
};

type Props = (
  | {
      data: z.infer<typeof ReportReviewSheetListModel>;
      sheetType: 'RS';
    }
  | {
      data: z.infer<typeof ReportSegmentSheetListModel>;
      sheetType: 'SS';
    }
  | {
      data: z.infer<typeof ReportFundSheetListModel>;
      sheetType: 'KS';
    }
) & {
  isDebugMode: boolean;
};

/** 負の値を含む集計値の場合に使用する*/
const createAmountByNegativeValue = ({
  amount,
  negativeCount,
  isDebugMode,
}: {
  amount: number | null | undefined;
  negativeCount: number | null | undefined;
  isDebugMode: boolean;
}) => {
  const { fallbackValue } = handleZeroValueWithNegativeCount(amount, {
    negativeCount,
    isDebugMode,
  });
  return fallbackValue;
};

const convertCsvRowData = ({ data, sheetType, isDebugMode }: Props) => {
  switch (sheetType) {
    case 'RS':
      return data.flatMap((d) => {
        // 型安全性を向上させるため、明示的に必要なプロパティを抽出
        const {
          paymentData,
          previousYearExecutionRate,
          organization,
          // RS固有のプロパティ
          skipAdministrativeReformTeamEvaluation,
          inspectionByExternalExpertReviewType,
          candidateReasonsOfInspectionByExternalExpert,
          inspectionByExternalExpertLastImplementedFiscalYear,
          // 共通プロパティを明示的に抽出
          id,
          sheetType: sheetTypeValue,
          fiscalYear,
          name,
          overview,
          isLocked,
          isPublished,
          startYear,
          endYear,
          purpose,
          task,
          projectNumber,
          ministryName,
          finalizedAmountForInitialBudget,
          negativeFinalizedAmountForInitialBudgetCount,
          finalizedAmountForSupplementaryBudget,
          negativeFinalizedAmountForSupplementaryBudgetCount,
          finalizedAmountForCarriedForward,
          negativeFinalizedAmountForCarriedForwardCount,
          finalizedAmountForReserve,
          negativeFinalizedAmountForReserveCount,
          finalizedAmount,
          negativeFinalizedAmountCount,
          requestedAmount,
          negativeRequestedAmountCount,
          previousYearExecutionAmount,
          negativePreviousYearExecutionAmountCount,
        } = d;

        return {
          id,
          sheetType: SheetTypeLabelModifier[sheetTypeValue],
          fiscalYear,
          name,
          organizationLabel:
            organization?.flatten?.names?.join(NEST_ITEM_CONNECTOR) ?? '',
          skipAdministrativeReformTeamEvaluation:
            skipAdministrativeReformTeamEvaluation === true
              ? t('is-not-excluded')
              : t('is-excluded'),
          inspectionByExternalExpertReviewType:
            inspectionByExternalExpertReviewType
              ? ReviewTypeLabelModifier[inspectionByExternalExpertReviewType]
              : '',
          candidateReasonsOfInspectionByExternalExpert:
            candidateReasonsOfInspectionByExternalExpert
              ?.map(
                (reason) =>
                  CandidateReasonOfInspectionByExternalExpertModifier[reason],
              )
              .join(', ') ?? '',
          inspectionByExternalExpertLastImplementedFiscalYear,
          overview: overview ?? '',
          isLocked: isLocked ? t('is-locked') : t('is-not-locked'),
          isPublished: isPublished ? t('published') : t('not-published'),
          ministryName,
          startYear,
          endYear,
          purpose: purpose ?? '',
          task: task ?? '',
          previousYearExecutionRate: formatNumber(previousYearExecutionRate, {
            maximumFractionDigits: 1,
          }),
          mainPayment: paymentData?.name ?? '',
          // 以下集計値
          previousYearExecutedTotalAmount: createAmountByNegativeValue({
            amount: previousYearExecutionAmount,
            negativeCount: negativePreviousYearExecutionAmountCount,
            isDebugMode,
          }),
          finalizedAmountForInitialBudget: createAmountByNegativeValue({
            amount: finalizedAmountForInitialBudget,
            negativeCount: negativeFinalizedAmountForInitialBudgetCount,
            isDebugMode,
          }),
          finalizedAmountForSupplementaryBudget: createAmountByNegativeValue({
            amount: finalizedAmountForSupplementaryBudget,
            negativeCount: negativeFinalizedAmountForSupplementaryBudgetCount,
            isDebugMode,
          }),
          finalizedAmountForCarriedForward: createAmountByNegativeValue({
            amount: finalizedAmountForCarriedForward,
            negativeCount: negativeFinalizedAmountForCarriedForwardCount,
            isDebugMode,
          }),
          finalizedAmountForReserve: createAmountByNegativeValue({
            amount: finalizedAmountForReserve,
            negativeCount: negativeFinalizedAmountForReserveCount,
            isDebugMode,
          }),
          finalizedAmount: createAmountByNegativeValue({
            amount: finalizedAmount,
            negativeCount: negativeFinalizedAmountCount,
            isDebugMode,
          }),
          requestedAmount: createAmountByNegativeValue({
            amount: requestedAmount,
            negativeCount: negativeRequestedAmountCount,
            isDebugMode,
          }),
          paymentAmount: createAmountByNegativeValue({
            amount: paymentData?.amount,
            negativeCount: paymentData?.negativeAmountCount,
            isDebugMode,
          }),
          projectNumber,
        };
      });
    case 'SS':
      return data.map((d) => {
        // 型安全性を向上させるため、明示的に必要なプロパティを抽出
        const {
          paymentData,
          previousYearExecutionRate,
          organization,
          // SS固有のプロパティ（NOTE: skipAdministrativeReformTeamEvaluationは使用しない）
          // 共通プロパティを明示的に抽出
          id,
          sheetType: sheetTypeValue,
          fiscalYear,
          name,
          overview,
          isLocked,
          isPublished,
          startYear,
          endYear,
          purpose,
          task,
          projectNumber,
          ministryName,
          finalizedAmountForInitialBudget,
          negativeFinalizedAmountForInitialBudgetCount,
          finalizedAmountForSupplementaryBudget,
          negativeFinalizedAmountForSupplementaryBudgetCount,
          requestedAmount,
          negativeRequestedAmountCount,
          previousYearExecutionAmount,
          negativePreviousYearExecutionAmountCount,
        } = d;

        return {
          id,
          sheetType: SheetTypeLabelModifier[sheetTypeValue],
          fiscalYear,
          name,
          organizationLabel:
            organization?.flatten?.names?.join(NEST_ITEM_CONNECTOR) ?? '',
          // NOTE: skipAdministrativeReformTeamEvaluation is always empty.
          // ref. https://github.com/weldrow-com/bns/issues/10505
          skipAdministrativeReformTeamEvaluation: '',
          overview: overview ?? '',
          isLocked: isLocked ? t('is-locked') : t('is-not-locked'),
          isPublished: isPublished ? t('published') : t('not-published'),
          ministryName,
          startYear,
          endYear,
          purpose: purpose ?? '',
          task: task ?? '',
          previousYearExecutionRate: formatNumber(previousYearExecutionRate, {
            maximumFractionDigits: 1,
          }),
          mainPayment: paymentData?.name ?? '',
          // 以下集計値
          previousYearExecutedTotalAmount: createAmountByNegativeValue({
            amount: previousYearExecutionAmount,
            negativeCount: negativePreviousYearExecutionAmountCount,
            isDebugMode,
          }),
          finalizedAmountForInitialBudget: createAmountByNegativeValue({
            amount: finalizedAmountForInitialBudget,
            negativeCount: negativeFinalizedAmountForInitialBudgetCount,
            isDebugMode,
          }),
          finalizedAmountForSupplementaryBudget: createAmountByNegativeValue({
            amount: finalizedAmountForSupplementaryBudget,
            negativeCount: negativeFinalizedAmountForSupplementaryBudgetCount,
            isDebugMode,
          }),
          requestedAmount: createAmountByNegativeValue({
            amount: requestedAmount,
            negativeCount: negativeRequestedAmountCount,
            isDebugMode,
          }),
          paymentAmount: createAmountByNegativeValue({
            amount: paymentData?.amount,
            negativeCount: paymentData?.negativeAmountCount,
            isDebugMode,
          }),
          projectNumber,
        };
      });
    case 'KS':
      return data.map((d) => {
        return {
          organizationLabel:
            d.organization?.flatten?.names?.join(NEST_ITEM_CONNECTOR) ?? '',
          sheetType: SheetTypeLabelModifier[d.sheetType],
          fundSheetType: d.fundAdditionalAttributes?.fundSheetType
            ? FundSheetTypeLabelModifier[
                d.fundAdditionalAttributes.fundSheetType
              ]
            : '',
          projectNumber: d.projectNumber?.toString() ?? '',
          fiscalYear: d.fiscalYear,
          fundSheetNumber:
            d.fundAdditionalAttributes?.fundSheetNumber?.toString() ?? '',
          fundSheetBranchNumber:
            d.fundAdditionalAttributes?.fundSheetBranchNumber?.toString() ?? '',
          name: d.name,
          overview: d.overview ?? '',
          isLocked: d.isLocked ? t('is-locked') : t('is-not-locked'),
          isPublished: d.isPublished ? t('published') : t('not-published'),
          ministryName: d.ministryName ?? '',
          startYear: d.startYear,
          endYear: d.endYear,
          purpose: d.purpose ?? '',
          task: d.task ?? '',
          fundOwner: d.fundAdditionalAttributes?.fundOwner ?? '',
          fiscalYearOfCreation:
            d.fundAdditionalAttributes?.compositionBackgrounds?.[0]?.fiscalYearOfCreation?.toString() ??
            '',
          endDateOfNewApplicationReceiving:
            d.fundAdditionalAttributes?.scheduledEnd
              ?.endDateOfNewApplicationReceiving ?? '',
          endDate: d.fundAdditionalAttributes?.scheduledEnd?.endDate ?? '',
          operationForms: d.fundAdditionalAttributes?.operationForms
            ? d.fundAdditionalAttributes.operationForms
                .map((form) => OperationFormLabelModifier[form])
                .join(', ')
            : '',
          businessForms: d.fundAdditionalAttributes?.businessForms
            ? d.fundAdditionalAttributes.businessForms
                .map((form) => BusinessFormLabelModifier[form])
                .join(', ')
            : '',
          beginningOfTheFundBalance:
            d.fundAdditionalIncomeAndExpenditure?.beginningOfTheFundBalance?.toString() ??
            '',
          ownershipRatio: formatOwnershipRatioPercentage(
            d.ownershipRatio,
            false,
          ),
          previousYearOfAmountReturnedToTheNationalTreasuryReturn:
            d.fundAdditionalIncomeAndExpenditure?.prior?.amountReturnedToTheNationalTreasury?.toString() ??
            '',
          previousYearOfAdministrativeExpenses:
            d.fundAdditionalIncomeAndExpenditure?.prior?.administrativeExpense?.toString() ??
            '',
          id: d.id,
          mainPayment: d.paymentData?.name ?? '',
          previousYearAdministrativeExpenseRate: formatNumber(
            d.previousYearAdministrativeExpenseRate,
            {
              maximumFractionDigits: 1,
            },
          ),
          previousYearDivergenceRate: formatNumber(
            d.previousYearDivergenceRate,
            {
              maximumFractionDigits: 1,
            },
          ),
          // 以下集計値
          paymentAmount: createAmountByNegativeValue({
            amount: d.paymentData?.amount,
            negativeCount: d.paymentData?.negativeAmountCount,
            isDebugMode,
          }),
          previousYearOfTotalExpense: createAmountByNegativeValue({
            amount: d.previousYearTotalExpense,
            negativeCount: d.negativePreviousYearTotalExpenseCount,
            isDebugMode,
          }),
          previousYearOfTotalIncome: createAmountByNegativeValue({
            amount: d.previousYearTotalIncome,
            negativeCount: d.negativePreviousYearTotalIncomeCount,
            isDebugMode,
          }),
        };
      });
  }
};

export const getRsProjectListCsvData = (props: Props) => {
  const { sheetType } = props;
  return createCsv({
    headerKeys: CSV_COLUMN_ORDER[sheetType] as unknown as string[],
    headerConverter: CSV_HEADER[sheetType] as unknown as Record<string, string>,
    body: convertCsvRowData(props) as unknown as Record<string, any>[],
  });
};

// 型安全性を保証するため、各CSVヘッダーの型を定義
type CommonCSVHeader = Record<
  keyof Omit<BaseCSVRow, 'skipAdministrativeReformTeamEvaluation'>,
  string
>;
type RSCSVHeader = CommonCSVHeader &
  Record<
    keyof Pick<
      RSCSVRow,
      | 'skipAdministrativeReformTeamEvaluation'
      | 'inspectionByExternalExpertLastImplementedFiscalYear'
      | 'inspectionByExternalExpertReviewType'
      | 'candidateReasonsOfInspectionByExternalExpert'
    >,
    string
  >;
type SSCSVHeader = Record<keyof SSCSVRow, string>;
type KSCSVHeader = Record<keyof KSCSVRow, string>;

const COMMON_CSV_HEADER = {
  sheetType: t('sheet-type'),
  projectNumber: t('project-number'),
  fiscalYear: t('fiscal-year'),
  name: t('project-name'),
  overview: t('project-list-csv-header-overview'),
  isLocked: t('lock-project'),
  isPublished: t('publish-ability'),
  ministryName: t('ministry'),
  startYear: t('start-fiscal-year'),
  endYear: t('end-fiscal-year'),
  organizationLabel: t('organization-in-charge'),
  purpose: t('project-purpose'),
  task: t('project-task'),
  finalizedAmountForInitialBudget: t('initial-finalized-amount'),
  previousYearExecutedTotalAmount: t('previous-fiscal-year-executed'),
  previousYearExecutionRate: t('previous-year-execution-rate'),
  finalizedAmountForSupplementaryBudget: t('supplementary-finalized-amount'),
  finalizedAmountForCarriedForward: t('carry-over-finalized-amount'),
  finalizedAmountForReserve: t('reserve-finalized-amount'),
  finalizedAmount: t('all-finalized-amount'),
  requestedAmount: t('all-requested-amount'),
  mainPayment: t('main-payment'),
  paymentAmount: t('amount-of-expenditures'),
  id: t('project-list-csv-header-id'),
};

const CSV_HEADER = {
  RS: {
    ...COMMON_CSV_HEADER,
    skipAdministrativeReformTeamEvaluation: t(
      'project-list-csv-header-skip-administrative-reform-team-evaluation',
    ),
    inspectionByExternalExpertLastImplementedFiscalYear: t(
      'projects.last-implemented-fiscal-year',
    ),
    inspectionByExternalExpertReviewType: t('inspection-target'),
    candidateReasonsOfInspectionByExternalExpert: t(
      'candidate-reasons-of-inspection-by-external-expert',
    ),
  } satisfies RSCSVHeader,
  SS: {
    ...COMMON_CSV_HEADER,
    skipAdministrativeReformTeamEvaluation: t(
      'project-list-csv-header-skip-administrative-reform-team-evaluation',
    ),
  } satisfies SSCSVHeader,
  KS: {
    organizationLabel: t('organization-in-charge'),
    sheetType: t('sheet-type'),
    fundSheetType: t('fund-sheet-type'),
    projectNumber: t('project-number'),
    fiscalYear: t('fiscal-year'),
    fundSheetNumber: t('fund-sheet-number'),
    fundSheetBranchNumber: t('fund-sheet-branch-number'),
    name: t('project-name'),
    overview: t('project-list-csv-header-overview'),
    isLocked: t('lock-project'),
    isPublished: t('publish-ability'),
    ministryName: t('ministry'),
    startYear: t('start-fiscal-year'),
    endYear: t('end-fiscal-year'),
    purpose: t('project-purpose'),
    task: t('project-task'),
    fundOwner: t('fund-owner'),
    fiscalYearOfCreation: t('fiscal-year-of-creation'),
    endDateOfNewApplicationReceiving: t('end-date-of-new-application'),
    endDate: t('fund-scheduled-end-period'),
    operationForms: t('operation-form'),
    businessForms: t('business-form'),
    beginningOfTheFundBalance: t(
      'fund-balance-at-the-end-of-previous-fiscal-year',
    ),
    ownershipRatio: t('ownership-ratio'),
    previousYearOfTotalIncome: t('previous-year-of-total-income'),
    previousYearOfAmountReturnedToTheNationalTreasuryReturn: t(
      'previous-year-of-amount-of-national-treasury-return',
    ),
    previousYearOfTotalExpense: t('previous-year-of-total-expense'),
    previousYearOfAdministrativeExpenses: t(
      'previous-year-of-administrative-expenses',
    ),
    id: t('project-list-csv-header-id'),
    mainPayment: t('main-payment'),
    previousYearAdministrativeExpenseRate: t(
      'previous-year-of-administrative-expenses-rate',
    ),
    previousYearDivergenceRate: t('previous-year-of-divergence-rate'),
    paymentAmount: t('amount-of-expenditures'),
  } satisfies KSCSVHeader,
};

// CSV_COLUMN_ORDERも型安全性を保証
const CSV_COLUMN_ORDER = {
  RS: [
    'fiscalYear',
    'projectNumber',
    'name',
    'ministryName',
    'organizationLabel',
    'purpose',
    'task',
    'overview',
    'startYear',
    'endYear',
    'previousYearExecutedTotalAmount',
    'finalizedAmountForInitialBudget',
    'finalizedAmountForSupplementaryBudget',
    'finalizedAmountForCarriedForward',
    'finalizedAmountForReserve',
    'finalizedAmount',
    'requestedAmount',
    'mainPayment',
    'paymentAmount',
    'sheetType',
    'skipAdministrativeReformTeamEvaluation',
    'inspectionByExternalExpertReviewType',
    'candidateReasonsOfInspectionByExternalExpert',
    'inspectionByExternalExpertLastImplementedFiscalYear',
    'isLocked',
    'isPublished',
    'previousYearExecutionRate',
    'id',
  ] as const satisfies ReadonlyArray<keyof RSCSVRow>,
  SS: [
    'fiscalYear',
    'projectNumber',
    'name',
    'ministryName',
    'organizationLabel',
    'purpose',
    'task',
    'overview',
    'startYear',
    'endYear',
    'previousYearExecutedTotalAmount',
    'finalizedAmountForInitialBudget',
    'finalizedAmountForSupplementaryBudget',
    'requestedAmount',
    'mainPayment',
    'paymentAmount',
    'sheetType',
    'skipAdministrativeReformTeamEvaluation',
    'isLocked',
    'isPublished',
    'previousYearExecutionRate',
    'id',
  ] as const satisfies ReadonlyArray<keyof SSCSVRow>,
  KS: [
    'sheetType',
    'fundSheetType',
    'fiscalYear',
    'projectNumber',
    'fundSheetNumber',
    'fundSheetBranchNumber',
    'ministryName',
    'name',
    'fundOwner',
    'overview',
    'fiscalYearOfCreation',
    'endDate',
    'endDateOfNewApplicationReceiving',
    'operationForms',
    'businessForms',
    'previousYearOfTotalIncome',
    'previousYearOfAdministrativeExpenses',
    'previousYearOfTotalExpense',
    'previousYearOfAmountReturnedToTheNationalTreasuryReturn',
    'beginningOfTheFundBalance',
    'ownershipRatio',
    'mainPayment',
    'paymentAmount',
    'isLocked',
    'isPublished',
    'previousYearAdministrativeExpenseRate',
    'previousYearDivergenceRate',
    'id',
  ] as const satisfies ReadonlyArray<keyof KSCSVRow>,
};
